import React, { useState, useMemo, useEffect, forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import './DataTable.css';
import FilterCell from './FilterCell';

const formatDate = (dateValue) => {
  if (!dateValue) return '';

  try {
    let date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      const sqlDateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (sqlDateMatch) {
        const [, year, month, day] = sqlDateMatch;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(dateValue);
      }

      if (isNaN(date.getTime())) {
        return dateValue;
      }
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return String(dateValue);
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    return String(dateValue);
  }
};

const formatSum = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return value ?? '';
  return num.toLocaleString('he-IL');
};

const COL_MAX_WIDTH = 'var(--datatable-col-max-width, 10vw)';
const COL_MIN_WIDTH = 56;
const COL_MAX_RESIZE = 520;

const getColumnKey = (col) => col.columnId || col.field;

const getColumnAlignClass = (col) => {
  if (col.type === 'sum' || col.type === 'number') return 'datatable-align-numeric';
  if (col.type === 'date') return 'datatable-align-date';
  return 'datatable-align-text';
};

const getCellText = (col, row) => {
  const raw = row[col.field];
  if (col.type === 'sum' || col.type === 'number') return formatSum(raw);
  if (col.type === 'date') return formatDate(raw);
  return String(raw ?? '');
};

const isStatusColumn = (col) => {
  if (col.type === 'status' || col.badge) return true;
  const label = String(col.label || col.field || '');
  return /סטטוס|status/i.test(label);
};

const getStatusBadgeClass = (value) => {
  const text = String(value || '').trim();
  if (/שודרג|תוספת/i.test(text)) return 'datatable-status-badge--green';
  if (/סגור|אושר|הושלם|סיום/i.test(text)) return 'datatable-status-badge--teal';
  if (/בטיפול|פתוח|ממתין|בתהליך/i.test(text)) return 'datatable-status-badge--tan';
  if (/לא נבחר/i.test(text)) return 'datatable-status-badge--gray';
  return 'datatable-status-badge--neutral';
};

const renderCellValue = (col, row, onLinkClick, onActionClick) => {
  const raw = row[col.field];

  if ((col.type === 'action' || col.type === 'icon' || col.type === 'button') && onActionClick) {
    return (
      <button
        type="button"
        className="datatable-action-btn"
        onClick={(event) => {
          event.stopPropagation();
          onActionClick(col, row, event);
        }}
      >
        {getCellText(col, row) || '+'}
      </button>
    );
  }

  if (col.type === 'link' && raw) {
    if (onLinkClick) {
      return (
        <button
          type="button"
          className="datatable-link-btn"
          onClick={(event) => {
            event.stopPropagation();
            onLinkClick(col, row, event);
          }}
        >
          {getCellText(col, row)}
        </button>
      );
    }

    return (
      <a href={raw} target="_blank" rel="noopener noreferrer">
        {getCellText(col, row)}
      </a>
    );
  }

  if (isStatusColumn(col) && raw !== null && raw !== undefined && String(raw).trim() !== '') {
    return (
      <span className={`datatable-status-badge ${getStatusBadgeClass(raw)}`}>
        {String(raw)}
      </span>
    );
  }

  return getCellText(col, row);
};

const DataTable = forwardRef(function DataTable(
  {
    columns,
    data,
    defaultPageSize = 20,
    search = '',
    onLinkClick,
    onActionClick,
    onRowDoubleClick,
    initialFilters,
    initialFiltersKey,
  },
  ref
) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: null, direction: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const resizeStateRef = useRef(null);

  const hasCustomWidths = Object.keys(columnWidths).length > 0;

  const getColumnWidthStyle = useCallback((col) => {
    const userWidth = columnWidths[col.field];
    if (userWidth) {
      return {
        width: `${userWidth}px`,
        minWidth: `${userWidth}px`,
        maxWidth: `${userWidth}px`,
      };
    }
    return {
      maxWidth: COL_MAX_WIDTH,
      ...(col.width ? { minWidth: col.width } : {}),
    };
  }, [columnWidths]);

  useEffect(() => {
    setColumnWidths({});
  }, [columns]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    if (initialFiltersKey === undefined) return;
    setFilters(initialFilters || {});
    setPage(1);
  }, [initialFiltersKey, initialFilters]);

  const filteredData = useMemo(() => {
    let filtered = data;

    if (search) {
      filtered = filtered.filter(row =>
        columns.some(col => String(row[col.field] || '').toLowerCase().includes(search.toLowerCase()))
      );
    }

    Object.entries(filters).forEach(([field, filterObj]) => {
      if (!filterObj) return;
      if (filterObj.select && Array.isArray(filterObj.select) && filterObj.select.length > 0) {
        filtered = filtered.filter(row => filterObj.select.includes(String(row[field] || '')));
      }
      if (filterObj.input) {
        filtered = filtered.filter(row => String(row[field] || '').toLowerCase().includes(filterObj.input.toLowerCase()));
      }
    });

    if (sort.field) {
      filtered = [...filtered].sort((a, b) => {
        const av = a[sort.field];
        const bv = b[sort.field];
        if (av === bv) return 0;
        if (sort.direction === 'asc') return av > bv ? 1 : -1;
        return av < bv ? 1 : -1;
      });
    }
    return filtered;
  }, [data, search, filters, sort, columns]);

  const summaryCells = useMemo(() => {
    const hasNumericColumns = columns.some(
      (col) => col.type === 'sum' || col.type === 'number'
    );
    if (!hasNumericColumns || filteredData.length === 0) return null;

    let labelPlaced = false;

    return columns.map((col) => {
      if (col.type === 'sum' || col.type === 'number') {
        const total = filteredData.reduce((acc, row) => {
          const num = Number(row[col.field]);
          return Number.isNaN(num) ? acc : acc + num;
        }, 0);
        return formatSum(total);
      }

      if (!labelPlaced) {
        labelPlaced = true;
        return 'סה"כ';
      }

      return '';
    });
  }, [columns, filteredData]);

  const pageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (field) => {
    setSort(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const handleFilter = (field, type, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], [type]: value }
    }));
    setPage(1);
  };

  const startColumnResize = useCallback((field, event) => {
    event.preventDefault();
    event.stopPropagation();

    const th = event.currentTarget.closest('th');
    if (!th) return;

    resizeStateRef.current = {
      field,
      startWidth: th.offsetWidth,
      startX: event.clientX,
    };

    const onMouseMove = (moveEvent) => {
      const state = resizeStateRef.current;
      if (!state) return;

      const nextWidth = Math.max(
        COL_MIN_WIDTH,
        Math.min(COL_MAX_RESIZE, state.startWidth + (state.startX - moveEvent.clientX))
      );

      setColumnWidths((prev) => ({
        ...prev,
        [state.field]: nextWidth,
      }));
    };

    const onMouseUp = () => {
      resizeStateRef.current = null;
      document.body.classList.remove('datatable-col-resizing');
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.body.classList.add('datatable-col-resizing');
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const resetColumnWidth = useCallback((field, event) => {
    event.preventDefault();
    event.stopPropagation();
    setColumnWidths((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const exportToExcel = () => {
    const BOM = '\uFEFF';

    const csv = [
      columns.map(col => col.label).join(','),
      ...filteredData.map(row => columns.map(col => {
        const escapedValue = getCellText(col, row).replace(/"/g, '""');
        return `"${escapedValue}"`;
      }).join(',')),
      ...(summaryCells
        ? [summaryCells.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')]
        : []),
    ].join('\n');

    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'report.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const printTable = () => {
    const cleanup = () => {
      document.body.classList.remove('printing-report');
      window.removeEventListener('afterprint', cleanup);
    };

    document.body.classList.add('printing-report');
    window.addEventListener('afterprint', cleanup);

    window.requestAnimationFrame(() => {
      window.print();
    });
  };

  useImperativeHandle(ref, () => ({
    exportToExcel,
    printTable,
    setFilters: (nextFilters) => {
      setFilters(nextFilters || {});
      setPage(1);
    },
  }));

  return (
    <div className="datatable-container">
      <div className="datatable-table-wrapper">
        <table className={`datatable-table${hasCustomWidths ? ' datatable-table--resized' : ''}`}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={getColumnKey(col)}
                  className={`datatable-th ${getColumnAlignClass(col)}`}
                  style={{ ...getColumnWidthStyle(col) }}
                  onDoubleClick={() => handleSort(col.field)}
                  title="לחיצה כפולה למיון"
                >
                  <span className="datatable-th-content">
                    {col.label}
                    {sort.field === col.field && (
                      <span className="datatable-sort-indicator">
                        {sort.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                    <FilterCell
                      column={col}
                      data={data}
                      value={filters[col.field]}
                      onChange={(type, value) => handleFilter(col.field, type, value)}
                      icon="filter"
                    />
                  </span>
                  <span
                    className="datatable-col-resizer"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`שינוי רוחב עמודה ${col.label}`}
                    title="גרור לשינוי רוחב. לחיצה כפולה לאיפוס"
                    onMouseDown={(event) => startColumnResize(col.field, event)}
                    onDoubleClick={(event) => resetColumnWidth(col.field, event)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="datatable-empty">
                  אין נתונים להצגה
                </td>
              </tr>
            ) : (
              pagedData.map((row, i) => (
                <tr
                  key={i}
                  className={selectedRowIndex === i ? 'datatable-row-selected' : ''}
                  onDoubleClick={() => onRowDoubleClick?.(row)}
                  title={onRowDoubleClick ? 'לחיצה כפולה לצפייה במסמכים' : undefined}
                >
                  {columns.map((col, j) => {
                    const titleText = getCellText(col, row);

                    return (
                      <td
                        key={getColumnKey(col)}
                        className={`datatable-td ${getColumnAlignClass(col)}`}
                        style={{
                          ...getColumnWidthStyle(col),
                          whiteSpace: col.wrap ? 'normal' : 'nowrap',
                        }}
                        title={titleText}
                        onClick={() => {
                          if (j <= 2) setSelectedRowIndex(selectedRowIndex === i ? null : i);
                        }}
                      >
                        {renderCellValue(col, row, onLinkClick, onActionClick)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
          {summaryCells && (
            <tfoot>
              <tr className="datatable-summary-row">
                {columns.map((col, index) => (
                  <td
                    key={getColumnKey(col)}
                    className={`datatable-td datatable-summary-cell ${getColumnAlignClass(col)}`}
                    style={getColumnWidthStyle(col)}
                  >
                    {summaryCells[index]}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="datatable-pagination">
        <button onClick={() => setPage(1)} disabled={page === 1} className="datatable-btn datatable-btn-page">{'<<'}</button>
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="datatable-btn datatable-btn-page">{'<'}</button>
        <span className="datatable-pagination-info">עמוד {page} מתוך {pageCount} | מספר שורות {filteredData.length}</span>
        <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page === pageCount} className="datatable-btn datatable-btn-page">{'>'}</button>
        <button onClick={() => setPage(pageCount)} disabled={page === pageCount} className="datatable-btn datatable-btn-page">{'>>'}</button>
        <div className="datatable-page-size">
          <span>הצג</span>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="datatable-select">
            {[10, 20, 30, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>בעמוד</span>
        </div>
      </div>
    </div>
  );
});

export default DataTable;
